import React, { useEffect, useMemo, useState } from "react";
import { apiPost, isInvalidSessionError } from "../api";
import { useAuth } from "../auth/AuthProvider";
import LinkButton from "../components/BaseButton/LinkButton";
import PageHeader from "../components/PageHeader";

function getDevicesLabel(devices) {
    if (devices === 1) {
        return "1 устройство";
    }

    if (devices < 5) {
        return `${devices} устройства`;
    }

    return `${devices} устройств`;
}

function formatRub(amount) {
    const value = Number(amount);
    if (!Number.isFinite(value)) {
        return "...";
    }

    return value.toLocaleString("ru-RU");
}

function TariffsRecalc() {
    const auth = useAuth();
    const [data, setData] = useState({
        current_devices: 1,
        expire: null,
        options: [],
    });
    const [status, setStatus] = useState({
        isLoading: true,
        error: "",
    });
    const [selectedDevices, setSelectedDevices] = useState(null);
    const getAppearStyle = () => ({ "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s` });

    useEffect(() => {
        let isMounted = true;

        async function loadPageData() {
            setStatus({ isLoading: true, error: "" });

            try {
                const response = await apiPost("/cabinet/change_devices_page_data", {}, auth);
                if (!isMounted) {
                    return;
                }

                const options = Array.isArray(response.options) ? response.options : [];
                setData({
                    current_devices: Number(response.current_devices) || 1,
                    expire: response.expire || null,
                    options,
                });
                setSelectedDevices(options[0]?.devices ?? null);
                setStatus({ isLoading: false, error: "" });
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                if (isInvalidSessionError(error)) {
                    auth.logout();
                    return;
                }

                setData({
                    current_devices: 1,
                    expire: null,
                    options: [],
                });
                setSelectedDevices(null);
                setStatus({
                    isLoading: false,
                    error: "Не удалось рассчитать доплату за смену устройств",
                });
            }
        }

        void loadPageData();

        return () => {
            isMounted = false;
        };
    }, [auth]);

    const selectedOption = useMemo(
        () => data.options.find((option) => option.devices === selectedDevices) || null,
        [data.options, selectedDevices],
    );

    const payState = selectedOption ? {
        devices: Number(selectedOption.devices),
        price: Number(selectedOption.amount_rub),
        orderType: "change_devices",
        orderLabel: "Перерасчет устройств",
    } : null;

    return (
        <>
            <PageHeader title="Расширение тарифа" />

            {status.error ? (
                <div className="gray-block pad1 shadow-1 menu-appear" style={getAppearStyle()}>
                    <span>{status.error}</span>
                </div>
            ) : null}

            <div className="gray-block pad1 shadow-1 flex-col gap05 menu-appear" style={getAppearStyle()}>
                <div className="flex-between">
                    <span className="grey">Сейчас в тарифе</span>
                    <b>{status.isLoading ? "..." : getDevicesLabel(data.current_devices)}</b>
                </div>
                <div className="flex-between">
                    <span className="grey">Оплачено до</span>
                    <b>{status.isLoading ? "..." : (data.expire || "Не указано")}</b>
                </div>
                <p className="grey font09" style={{ margin: 0 }}>
                    Выберите новый лимит устройств. Под каждой плиткой показана сумма доплаты за весь уже оплаченный остаток подписки.
                </p>
            </div>

            {!status.isLoading && !data.options.length ? (
                <div className="gray-block pad1 shadow-1 menu-appear" style={getAppearStyle()}>
                    <span>У вас уже максимальный тариф по количеству устройств.</span>
                </div>
            ) : null}

            {!!data.options.length ? (
                <div className="tarifs-grid gap1">
                    {data.options.map((option) => {
                        const isSelected = selectedDevices === option.devices;

                        return (
                            <button
                                key={option.devices}
                                type="button"
                                className={`btn-month pad1 flex-col flex-center w100 shadow-1 menu-appear ${
                                    isSelected ? "selected" : ""
                                }`}
                                onClick={() => setSelectedDevices(option.devices)}
                                data-selected={isSelected}
                                style={getAppearStyle()}
                            >
                                <div className="tarifs-txt w100 flex-col flex-center gap05">
                                    <span>{getDevicesLabel(option.devices)}</span>
                                    <span className="grey font08">доплата</span>
                                    <div>
                                        <span className="font17">{formatRub(option.amount_rub)}</span>
                                        <span className="grey font11"> ₽</span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            <LinkButton
                to="/paytype"
                state={payState}
                disabled={!payState}
                className="menu-appear"
                style={getAppearStyle()}
            >
                {selectedOption ? (
                    <span>
                        <b>Перейти к оплате </b>
                        <span id="price_btn_text">{formatRub(selectedOption.amount_rub)}</span>
                        <span className="grey" style={{ fontSize: "0.75em", fontWeight: "bold" }}> ₽</span>
                    </span>
                ) : (
                    <b>Выберите новый лимит устройств</b>
                )}
            </LinkButton>
        </>
    );
}

export default TariffsRecalc;
